/**
 * Hilo de comentarios de una cuenta, con @menciones al equipo.
 *
 * El hilo se lee de `/api/comments` y no del payload de la ficha a propósito:
 * `useCreateComment` invalida esa clave, así que al publicar el hilo se refresca
 * solo. Si lo leyera de la ficha, habría que acordarse de invalidar otra clave
 * en cada lugar que comente.
 */
import type { Comment, UserRef } from '@ops/contracts'
import { useRef, useState, type KeyboardEvent } from 'react'

import { mensajeDeError } from '../../lib/api'
import { formatRelativeDays, iniciales } from '../../lib/format'
import { useComments, useCreateComment, useUsers } from '../../lib/queries'
import { useToast } from '../../shell'
import { Button, Icon, Stack } from '../../ui'
import { Cargando, ErrorDeCarga, Vacio } from '../comunes'
import {
  candidatosMencion,
  insertarMencion,
  mencionesEn,
  partirPorMenciones,
  tokenMencionEn,
} from './menciones'

export interface ComentariosClienteProps {
  clientId: string
  clientNombre: string
  ahora: Date
}

export function ComentariosCliente({
  clientId,
  clientNombre,
  ahora,
}: ComentariosClienteProps) {
  const comentarios = useComments('client', clientId)
  const usuarios = useUsers()
  const equipo = usuarios.data ?? []

  return (
    <Stack gap="sm">
      {comentarios.isPending ? <Cargando que="el hilo del equipo" /> : null}

      {comentarios.isError ? (
        <ErrorDeCarga
          que="el hilo del equipo"
          error={comentarios.error}
          onRetry={() => void comentarios.refetch()}
          reintentando={comentarios.isFetching}
        />
      ) : null}

      {comentarios.isSuccess && comentarios.data.length === 0 ? (
        <Vacio punteada
          mensaje="Todavía nadie comentó esta cuenta."
          detalle="Lo que se escriba acá queda con la cuenta, no en un chat."
        />
      ) : null}

      {comentarios.isSuccess && comentarios.data.length > 0 ? (
        <ul className="flex list-none flex-col gap-3 p-0">
          {comentarios.data.map((comentario) => (
            <ComentarioItem
              key={comentario.id}
              comentario={comentario}
              equipo={equipo}
              ahora={ahora}
            />
          ))}
        </ul>
      ) : null}

      <Composer clientId={clientId} clientNombre={clientNombre} equipo={equipo} />
    </Stack>
  )
}

interface ComentarioItemProps {
  comentario: Comment
  equipo: readonly UserRef[]
  ahora: Date
}

function ComentarioItem({ comentario, equipo, ahora }: ComentarioItemProps) {
  return (
    <li data-testid="comentario" className="grid grid-cols-[26px_1fr] gap-2">
      <span
        aria-hidden="true"
        className="grid h-[26px] w-[26px] place-items-center rounded-full border border-border bg-n100 text-micro font-semibold text-text"
      >
        {iniciales(comentario.author.nombre)}
      </span>
      <div className="min-w-0">
        <p className="flex flex-wrap items-baseline gap-x-2 text-meta">
          <span className="font-semibold text-text">{comentario.author.nombre}</span>
          <span className="text-micro text-muted-strong">
            {formatRelativeDays(comentario.createdAt, ahora)}
          </span>
        </p>
        <p className="mt-0.5 whitespace-pre-wrap text-meta text-text">
          {partirPorMenciones(comentario.cuerpo, equipo).map((trozo, indice) =>
            trozo.mencion ? (
              <span
                // El índice alcanza: los trozos se derivan del cuerpo, que no se
                // reordena.
                key={`${indice}-${trozo.texto}`}
                className="rounded bg-accent-soft px-0.5 font-semibold text-accent-text"
              >
                {trozo.texto}
              </span>
            ) : (
              <span key={`${indice}-texto`}>{trozo.texto}</span>
            ),
          )}
        </p>
      </div>
    </li>
  )
}

interface ComposerProps {
  clientId: string
  clientNombre: string
  equipo: readonly UserRef[]
}

function Composer({ clientId, clientNombre, equipo }: ComposerProps) {
  const [cuerpo, setCuerpo] = useState('')
  const [cursor, setCursor] = useState(0)
  const [menuAbierto, setMenuAbierto] = useState(false)
  const [resaltado, setResaltado] = useState(0)
  const areaRef = useRef<HTMLTextAreaElement>(null)
  const crear = useCreateComment()
  const toast = useToast()

  const token = menuAbierto ? tokenMencionEn(cuerpo, cursor) : null
  const candidatos =
    token === null ? [] : candidatosMencion(equipo, token.consulta).slice(0, 5)

  function sincronizar(valor: string, posicion: number) {
    setCuerpo(valor)
    setCursor(posicion)
    const nuevo = tokenMencionEn(valor, posicion)
    setMenuAbierto(nuevo !== null)
    setResaltado(0)
  }

  function elegir(nombre: string) {
    const resultado = insertarMencion(cuerpo, cursor, nombre)
    setCuerpo(resultado.texto)
    setCursor(resultado.cursor)
    setMenuAbierto(false)
    const area = areaRef.current
    if (area !== null) {
      area.focus()
      // El valor todavía no llegó al DOM cuando esto corre; lo pone React en el
      // próximo render, así que la selección se fija después.
      window.requestAnimationFrame(() => {
        area.setSelectionRange(resultado.cursor, resultado.cursor)
      })
    }
  }

  function publicar() {
    const texto = cuerpo.trim()
    if (texto === '') return
    crear.mutate(
      {
        entityType: 'client',
        entityId: clientId,
        cuerpo: texto,
        mentions: mencionesEn(texto, equipo),
      },
      {
        onSuccess: () => {
          setCuerpo('')
          setCursor(0)
          toast.push('ok', `Se publicó tu comentario en ${clientNombre}.`)
        },
      },
    )
  }

  function onKeyDown(evento: KeyboardEvent<HTMLTextAreaElement>) {
    if (menuAbierto && candidatos.length > 0) {
      if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
        evento.preventDefault()
        const paso = evento.key === 'ArrowDown' ? 1 : candidatos.length - 1
        setResaltado((actual) => (actual + paso) % candidatos.length)
        return
      }
      if (evento.key === 'Enter' || evento.key === 'Tab') {
        evento.preventDefault()
        const elegido = candidatos[resaltado] ?? candidatos[0]
        if (elegido !== undefined) elegir(elegido.nombre)
        return
      }
      if (evento.key === 'Escape') {
        // No cerramos el drawer: el Escape le corresponde al menú abierto.
        evento.stopPropagation()
        setMenuAbierto(false)
        return
      }
    }
    if (evento.key === 'Enter' && (evento.metaKey || evento.ctrlKey)) {
      evento.preventDefault()
      publicar()
    }
  }

  return (
    <div className="relative">
      <label htmlFor={`comentario-${clientId}`} className="sr-only">
        Nuevo comentario sobre {clientNombre}
      </label>
      <textarea
        id={`comentario-${clientId}`}
        ref={areaRef}
        value={cuerpo}
        onChange={(evento) =>
          sincronizar(evento.target.value, evento.target.selectionStart)
        }
        onKeyDown={onKeyDown}
        placeholder="Escribí un comentario. Usá @ para mencionar a alguien del equipo."
        className="min-h-[58px] w-full resize-y rounded-md border border-border bg-surface px-2 py-1.5 text-meta text-text placeholder:text-muted hover:border-n300"
      />

      {menuAbierto && candidatos.length > 0 ? (
        <ul
          data-testid="menu-menciones"
          className="absolute bottom-full left-2 z-10 mb-1 flex list-none flex-col gap-0.5 rounded-md border border-border bg-surface p-1 shadow-pop"
        >
          {candidatos.map((usuario, indice) => (
            <li key={usuario.id}>
              <button
                type="button"
                onMouseDown={(evento) => {
                  evento.preventDefault()
                  elegir(usuario.nombre)
                }}
                aria-current={indice === resaltado ? true : undefined}
                className={
                  indice === resaltado
                    ? 'flex w-full items-center gap-2 rounded bg-accent-soft px-2 py-1 text-left text-meta font-semibold text-accent-text'
                    : 'flex w-full items-center gap-2 rounded px-2 py-1 text-left text-meta text-text hover:bg-n100'
                }
              >
                <Icon name="persona" size={11} />
                {usuario.nombre}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {crear.isError ? (
        <p role="alert" className="mt-1.5 text-meta text-danger-text">
          No se pudo publicar: {mensajeDeError(crear.error)}
        </p>
      ) : null}

      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <span className="mr-auto text-micro text-muted-strong">
          @ menciona a alguien · Ctrl+Enter publica
        </span>
        <Button
          variante="primario"
          onClick={publicar}
          cargando={crear.isPending}
          disabled={cuerpo.trim() === ''}
        >
          Comentar
        </Button>
      </div>
    </div>
  )
}
